<template>
  <router-view />
  <Toast position="bottom-right" />
  <ConfirmDialog>
    <!-- Replaces PrimeVue's default icon + message with the same flat markup and Aura classes inside
         .p-dialog-content. The default renders the `icon` option as a CSS class (PrimeIcons, not installed),
         so it is rendered here as a Material Icons ligature instead; pre-line keeps the line breaks
         (blank-line paragraphs) that some confirmation messages carry. -->
    <template #message="{ message }">
      <span v-if="message.icon" class="material-icons p-confirmdialog-icon" aria-hidden="true">{{ message.icon }}</span>
      <span class="p-confirmdialog-message" style="white-space: pre-line">{{ message.message }}</span>
    </template>
  </ConfirmDialog>
</template>

<script setup>
import { watch, inject, onMounted, onUnmounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useUser } from '@clerk/vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { identifyUmami } from './plugins/umami';
import { useTokenStore } from './composables/useTokenStore';

const { user } = useUser();
const rollbar = inject('rollbar', null);
const toast = useToast();
const confirm = useConfirm();
const router = useRouter();
const { store: tokenStore } = useTokenStore();

// Close an open confirmation when the route path changes (e.g. browser Back while it is up), so it
// cannot outlive the view that asked for it. Query-only changes keep it open. confirm.close() hides
// the dialog without calling the confirmation's accept / reject / onHide callbacks.
const removeConfirmRouteHook = router.afterEach((to, from, failure) => {
  if (!failure && to.path !== from.path) confirm.close();
});
onBeforeUnmount(removeConfirmRouteHook);

// Email domains that mark a user as internal (Athics staff). Matches the backend's
// existing @athics.ai check (services/activeCampaignService.js). The email is read
// only to derive the cohort below and is never sent to Umami.
const INTERNAL_EMAIL_DOMAINS = ['athics.ai'];

watch(user, (u) => {
  if (rollbar && u) {
    rollbar.configure({ payload: { person: { id: u.id } } }); // Clerk user ID for Rollbar
  }
}, { immediate: true });

// Umami: the same pseudonymous Clerk id (to correlate sessions with Rollbar errors)
// plus a PII-free cohort `account_type`:
//   internal      → Athics staff (email domain, checked locally, never sent)
//   client        → external user whose billing Athics manages (workspace.billing.managed)
//   self_service  → external user who self-manages billing
// `managed` is workspace-scoped and only known once the token store has loaded
// (tokenStore.lastUpdatedAt), so external users are identified id-only until then,
// then re-identified with their account_type once billing resolves or the workspace changes.
watch(
  [user, () => tokenStore.managed, () => tokenStore.lastUpdatedAt],
  ([u]) => {
    if (!u) return;
    const email = (u.primaryEmailAddress?.emailAddress || u.email || '').toLowerCase();
    const isInternal = INTERNAL_EMAIL_DOMAINS.some((d) => email.endsWith(`@${d}`));
    if (isInternal) {
      identifyUmami(u.id, 'internal');
    } else if (tokenStore.lastUpdatedAt) {
      identifyUmami(u.id, tokenStore.managed ? 'client' : 'self_service');
    } else {
      identifyUmami(u.id);
    }
  },
  { immediate: true },
);

// Bridge per i toast emessi via window event da services/toastService.js
// (l'unico listener era AdminDashboard.vue, eliminato dal refactor 93f0a56).
// detail: { message, severity }; una stringa semplice (formato legacy) vale come severity 'info'.
const onGlobalToast = (e) => {
  const detail = e?.detail;
  if (!detail) return;
  const { message, severity = 'info' } = typeof detail === 'string' ? { message: detail } : detail;
  if (message) toast.add({ severity, summary: String(message), life: 3000 });
};
onMounted(() => window.addEventListener('app-show-toast', onGlobalToast));
onUnmounted(() => window.removeEventListener('app-show-toast', onGlobalToast));
</script>
